/** AI console — the campaign's voice-agent setup, made simple for non-technical
 *  users: an easy builder (what the AI says + success/fallback actions) that the
 *  backend compiles into the ElevenLabs prompt, plus a collapsed "Advanced" raw-
 *  prompt escape hatch. See docs/ai-agent/CAMPAIGN_CONFIGURABLE_AGENT.md. */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useHasPermission } from "@/lib/permissions";

import { type AgentConfig, getAgentSettings, updateAgentSettings } from "./api";

export function AgentSettingsPanel({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useHasPermission("campaign.config");
  const [cfg, setCfg] = useState<AgentConfig | null>(null);
  const [raw, setRaw] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const introRef = useRef<HTMLTextAreaElement>(null);
  const goalRef = useRef<HTMLTextAreaElement>(null);
  const [focused, setFocused] = useState<"intro" | "goal">("intro");

  const settings = useQuery({
    queryKey: ["ai-agent-settings", campaignId],
    queryFn: () => getAgentSettings(campaignId),
  });

  useEffect(() => {
    if (settings.data && cfg === null) {
      setCfg(settings.data.ai_agent_config);
      setRaw(settings.data.ai_agent_prompt);
      setShowAdvanced(settings.data.advanced_active);
    }
  }, [settings.data, cfg]);

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof updateAgentSettings>[1]) =>
      updateAgentSettings(campaignId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-agent-settings", campaignId] }),
  });

  if (settings.isLoading || cfg === null || !settings.data) {
    return (
      <section className="card">
        <h2 className="mb-2 text-sm font-semibold text-nb-text">{t("aiAgent.title")}</h2>
        <p className="text-sm text-nb-text-3">…</p>
      </section>
    );
  }

  const d = settings.data;
  const set = (patch: Partial<AgentConfig>) => setCfg({ ...cfg, ...patch });

  const insertVar = (key: string) => {
    const ref = focused === "goal" ? goalRef : introRef;
    const field = focused;
    const token = `{{${key}}}`;
    const el = ref.current;
    const cur = cfg[field] ?? "";
    if (!el) {
      set({ [field]: cur + token } as Partial<AgentConfig>);
      return;
    }
    const s = el.selectionStart ?? cur.length;
    const e = el.selectionEnd ?? cur.length;
    set({ [field]: cur.slice(0, s) + token + cur.slice(e) } as Partial<AgentConfig>);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = s + token.length;
    });
  };

  const campaignVars = d.available_variables.campaign.map((v) => v.key);

  return (
    <section className="card space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-nb-text">{t("aiAgent.title")}</h2>
        <p className="text-sm text-nb-text-2">{t("aiAgent.subtitle")}</p>
      </div>

      {/* When a raw prompt is set, the builder is bypassed — tell the user. */}
      {d.advanced_active && (
        <p className="rounded-nb-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
          {t("aiAgent.advancedActive")}
        </p>
      )}

      {/* ---- Easy builder ---- */}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="label">{t("aiAgent.language")}</span>
          <select
            className="input"
            value={cfg.language}
            onChange={(e) => set({ language: e.target.value })}
            disabled={!canEdit}
          >
            {d.options.languages.map((l) => (
              <option key={l} value={l}>
                {t(`aiAgent.lang_${l}`, l.toUpperCase())}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="flex flex-col gap-1">
        <span className="label">{t("aiAgent.whatToSay")}</span>
        <span className="text-xs text-nb-text-3">{t("aiAgent.whatToSayHint")}</span>
        <textarea
          ref={introRef}
          className="input min-h-[5rem] w-full"
          value={cfg.intro}
          onFocus={() => setFocused("intro")}
          onChange={(e) => set({ intro: e.target.value })}
          disabled={!canEdit}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="label">{t("aiAgent.goalLabel")}</span>
        <span className="text-xs text-nb-text-3">{t("aiAgent.goalHint")}</span>
        <textarea
          ref={goalRef}
          className="input min-h-[4rem] w-full"
          value={cfg.goal}
          onFocus={() => setFocused("goal")}
          onChange={(e) => set({ goal: e.target.value })}
          disabled={!canEdit}
        />
      </label>

      {/* Variable chips insert into the last-focused field */}
      <div className="space-y-2">
        <p className="text-xs text-nb-text-3">{t("aiAgent.insertHint")}</p>
        <VarGroup label={t("aiAgent.campaignVars")} keys={campaignVars} onInsert={canEdit ? insertVar : undefined} empty={t("aiAgent.noCampaignVars")} />
        <VarGroup label={t("aiAgent.coreVars")} keys={d.available_variables.core} onInsert={canEdit ? insertVar : undefined} />
      </div>

      {/* When the call goes well there are TWO parallel outcomes — the person is
          available now, or wants a scheduled time. Both are configured here. */}
      <div className="space-y-3">
        <div>
          <p className="label">{t("aiAgent.successHeading")}</p>
          <p className="text-xs text-nb-text-3">{t("aiAgent.successHint")}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="label">{t("aiAgent.ifAvailableNow")}</span>
            <select className="input" value={cfg.success_now_action} onChange={(e) => set({ success_now_action: e.target.value })} disabled={!canEdit}>
              {d.options.success_now_actions.map((a) => (
                <option key={a} value={a}>{t(`aiAgent.action_${a}`, a)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="label">{t("aiAgent.ifScheduled")}</span>
            <select className="input" value={cfg.success_scheduled_action} onChange={(e) => set({ success_scheduled_action: e.target.value })} disabled={!canEdit}>
              {d.options.success_scheduled_actions.map((a) => (
                <option key={a} value={a}>{t(`aiAgent.action_${a}`, a)}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <label className="flex flex-col gap-1">
        <span className="label">{t("aiAgent.ifFallback")}</span>
        <select className="input sm:max-w-xs" value={cfg.fallback_action} onChange={(e) => set({ fallback_action: e.target.value })} disabled={!canEdit}>
          {d.options.fallback_actions.map((a) => (
            <option key={a} value={a}>{t(`aiAgent.action_${a}`, a)}</option>
          ))}
        </select>
      </label>

      {canEdit && (
        <div className="flex items-center gap-3">
          <button type="button" className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate({ ai_agent_config: cfg })}>
            {t("aiAgent.save")}
          </button>
          {save.isSuccess && <span className="text-xs text-nb-mint">{t("aiAgent.saved")}</span>}
        </div>
      )}

      {/* ---- Preview of the compiled prompt ---- */}
      <div className="border-t border-nb-border/50 pt-3">
        <button type="button" className="text-xs text-nb-text-2 hover:underline" onClick={() => setShowPreview((v) => !v)}>
          {showPreview ? "▾ " : "▸ "}{t("aiAgent.previewLabel")}
        </button>
        {showPreview && (
          <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-nb-lg bg-nb-bg/60 p-3 font-mono text-[11px] text-nb-text-2">
            {d.compiled_preview}
          </pre>
        )}
      </div>

      {/* ---- Advanced: raw prompt ---- */}
      <div className="border-t border-nb-border/50 pt-3">
        <button type="button" className="text-xs text-nb-text-2 hover:underline" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "▾ " : "▸ "}{t("aiAgent.advancedTitle")}
        </button>
        {showAdvanced && (
          <div className="mt-2 space-y-2">
            <p className="text-xs text-nb-text-3">{t("aiAgent.advancedHint")}</p>
            <textarea
              className="input min-h-[12rem] w-full font-mono text-xs"
              value={raw ?? ""}
              onChange={(e) => setRaw(e.target.value)}
              disabled={!canEdit}
            />
            {canEdit && (
              <button type="button" className="btn btn-ghost" disabled={save.isPending} onClick={() => save.mutate({ ai_agent_prompt: raw ?? "" })}>
                {t("aiAgent.save")}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function VarGroup({
  label,
  keys,
  onInsert,
  empty,
}: {
  label: string;
  keys: string[];
  onInsert?: (key: string) => void;
  empty?: string;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-1.5">
      <span className="mr-1 text-xs font-semibold text-nb-text-2">{label}:</span>
      {keys.length === 0 && empty && <span className="text-xs text-nb-text-3">{empty}</span>}
      {keys.map((k) =>
        onInsert ? (
          <button key={k} type="button" className="pill font-mono text-xs hover:bg-nb-mint/20" onClick={() => onInsert(k)}>
            {k}
          </button>
        ) : (
          <span key={k} className="pill font-mono text-xs">{k}</span>
        ),
      )}
    </div>
  );
}
